import { Chip, IconButton, Row, Tooltip } from "@narmi/design_system";
import { SavedReply } from "@pages/configuration/messages/types";
import { useRef, useState } from "react";
import { useFileUpload } from "../hooks/useFileUpload";
import { useThread } from "../hooks/useThread";
import { AiDraft } from "../types";
import { ActionBar } from "./ActionBar";
import styles from "./style.module.css";
import { ThreadFile } from "./ThreadFile";
import { ReplyBoxContents } from "./ReplyBoxContents";

export function ReplyBox() {
  const { reply, isSubmitting, aiDraft, aiDraftAvailable, isRegenerating } = useThread();

  const { files, removeFile, fileInputRef, isDragActive, handleDragEvent, handleDrop } =
    useFileUpload();

  const [savedReplyPreview, setSavedReplyPreview] = useState<SavedReply | null>(null);

  const [isInternalNoteMode, setIsInternalNoteMode] = useState(false);

  const [textareaHeight, setTextareaHeight] = useState<number | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Drag anywhere on the banner or the reply body to resize the textarea:
  // dragging up grows it, dragging down collapses it, clamped to 10–300px.
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    if (
      e.target instanceof Element &&
      e.target.closest('textarea, input, select, button, a, [contenteditable], [role="button"]')
    ) {
      return;
    }
    const startY = e.clientY;
    const startHeight = textareaRef.current?.getBoundingClientRect().height ?? 0;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const delta = startY - moveEvent.clientY;
      setTextareaHeight(Math.min(300, Math.max(10, startHeight + delta)));
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // Swallow drag events while submitting so the browser does not open dropped
  // files, without adding them to a draft that is on its way out.
  const ignoreDragEvent = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOnAcceptSavedReply = () => {
    reply.setValue((prev) => {
      prev = aiDraftAvailable && !reply.value.trim() ? (aiDraft?.body ?? "") : prev;
      prev = prev.trim();
      return prev ? prev + "\n\n--------\n\n" + savedReplyPreview!.body : savedReplyPreview!.body;
    });
    setSavedReplyPreview(null);
  };

  return (
    <div
      className={`rounded--all ${styles.replyBox} ${aiDraftAvailable ? "bgColor--cloudGrey border--all" : ""}`}
      onDragEnter={isSubmitting ? ignoreDragEvent : handleDragEvent}
      onDragOver={isSubmitting ? ignoreDragEvent : handleDragEvent}
      onDragLeave={isSubmitting ? ignoreDragEvent : handleDragEvent}
      onDrop={isSubmitting ? ignoreDragEvent : handleDrop}
    >
      {isDragActive && !isSubmitting && (
        <div className={styles.dropOverlay}>
          <Row alignItems="center" justifyContent="center" gapSize="xs">
            <Row.Item shrink>
              <span className="narmi-icon-upload-cloud fontSize--heading2" />
            </Row.Item>
            <Row.Item shrink>Drop files to attach</Row.Item>
          </Row>
        </div>
      )}
      {aiDraftAvailable && (aiDraft?.status === "ready" || aiDraft?.status === "failed") && (
        <div onMouseDown={handleResizeMouseDown} style={{ cursor: "ns-resize" }}>
          <AiDraftContainerBanner aiDraft={aiDraft!} reply={reply.value} />
        </div>
      )}
      <div
        className={`bgColor--white rounded--all padding--all ${aiDraftAvailable ? "" : "border--all"}`}
        onMouseDown={handleResizeMouseDown}
      >
        <div className={`${reply.error ? styles.error : ""}`}>
          <ReplyBoxContents
            isInternalNoteMode={isInternalNoteMode}
            savedReplyPreview={savedReplyPreview}
            setSavedReplyPreview={setSavedReplyPreview}
            onTextChange={reply.setValue}
            onSavedReplyAccept={handleOnAcceptSavedReply}
            textareaRef={textareaRef}
            textareaHeight={textareaHeight}
          />
          {files.length > 0 && (
            <div className={styles.filePreviewContainer}>
              {files.map((file) => (
                <ThreadFile
                  key={file.name}
                  label={file.name}
                  url={file.previewUrl}
                  endContent={
                    <Tooltip text="Remove file">
                      <IconButton
                        kind="action"
                        name="x"
                        disabled={isSubmitting}
                        onClick={(e: React.MouseEvent) => {
                          e.preventDefault();
                          removeFile(file);
                        }}
                        textSize="s"
                      />
                    </Tooltip>
                  }
                />
              ))}
            </div>
          )}
          {!isRegenerating && (
            <ActionBar
              setSavedReplyPreview={setSavedReplyPreview}
              ref={fileInputRef}
              setIsInternalNoteMode={setIsInternalNoteMode}
            />
          )}
        </div>
        {reply.error && <div className={styles.errorMessage}>{reply.error}</div>}
      </div>
    </div>
  );
}

const AiDraftContainerBanner = ({ aiDraft, reply }: { aiDraft: AiDraft; reply: string }) => {
  return (
    <>
      {aiDraft.status === "failed" ? (
        <div className={styles.draftedByAiContainer}>
          <div className={styles.draftedByAiText}>Draft generation failed</div>
          <Tooltip text="Draft generation failed.">
            <Chip label="Failed" kind="error" />
          </Tooltip>
        </div>
      ) : (
        <div className={styles.draftedByAiContainer}>
          <div className={styles.draftedByAiText}>
            {!reply.trim() ? "Drafted by Narmi AI" : "Initially drafted by Narmi AI"}
          </div>
          {!reply.trim() ? (
            <ConfidenceScoreChip confidence={aiDraft?.confidence ?? null} />
          ) : (
            <Tooltip text="This message is an edited reply initially drafted by Narmi AI">
              <Chip label="Edited" kind="info" />
            </Tooltip>
          )}
        </div>
      )}
    </>
  );
};

const ConfidenceScoreChip = ({ confidence }: { confidence: number | null }) => {
  return confidence === null ? (
    <Tooltip text="Confidence score unavailable. Manual review needed.">
      <Chip label="Confidence score unavailable" kind="info" />
    </Tooltip>
  ) : confidence >= 0.8 ? (
    <Tooltip text="High confidence. Light review needed.">
      <Chip label="High confidence" kind="success" />
    </Tooltip>
  ) : confidence >= 0.5 ? (
    <Tooltip text="Medium confidence. Review advised.">
      <Chip label="Medium confidence" kind="warn" />
    </Tooltip>
  ) : (
    <Tooltip text="Low confidence. Manual review needed.">
      <Chip label="Low confidence" kind="error" />
    </Tooltip>
  );
};
